import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Box, IconButton } from '@mui/material';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import { useAtom, useAtomValue } from 'jotai';
import { useLocation } from 'react-router-dom';
import { selectedBranchAtom, searchValuesAtom } from '../../../Atoms';
import useAPI from '../../../api';
import useInfiniteQuery from '../../../Hooks/useInfiniteQuery';
import useTranslate from '../../../Hooks/useTranslate';
import useSectionLayout from '../../../Hooks/useSectionLayout';
import mapFilters from '../../../Helpers/Filters/mapFilters';
import { CALL_STANDARD_FILTERS } from '../../../Helpers/Filters/constants';
import { CHANNEL_TYPES } from '../../../Utils/constants/general';
import { resolveCard } from '../../engine/columns/resolveCard';
import {
  resolveStandardFilters,
  resolveRangeFilters,
} from '../../engine/filters/resolveFilters';
import { resolveSearchKeys } from '../../engine/search/resolveSearch';
import {
  LONGCHANNEL_SECTION,
  LONGCHANNEL_SEARCH,
} from '../../../Pages/AllCustomers/Utils/constants';
import { LONGCHANNEL_COLUMNS } from '../../../Pages/AllCustomers/Utils/longChannelColumnsSchema';
import { LONGCHANNEL_FILTERS_LAYOUT } from '../../../Pages/AllCustomers/Utils/longChannelFiltersSchema';
import LongChannelFilters from '../../../Pages/AllCustomers/Components/Filters';
import { BIRTHDAYS_FILTERS } from '../../../Pages/Birthdays/Components';
import { atDialect } from '../../engine/dialect';
import { ColumnFlexBox } from '../../FlexBox';
import MobileHeader from '../MobileHeader';
import ContactCard from '../ContactCard';
import ListSheet from '../ListSheet';
import FilterSheet from '../FilterSheet';
import SkeletonList from '../SkeletonList';
import useMobileNavigation from '../useMobileNavigation';

const SEARCH_BOX_FILTERS = [
  'fullNameSearch',
  'fiscalCode',
  'mobilePhoneNumber',
];
const RANGE_FILTERS = [{ keyName: 'distanceKm' }];

/** The birthdays page's filters, as the layout document the hook reads a default from. */
const BIRTHDAYS_FILTERS_LAYOUT = atDialect(BIRTHDAYS_FILTERS);

// Per-list getCustomers descriptor (mirrors the desktop pages).
const LIST_DESCRIPTOR = {
  CUSTOMERS: { type: 'All', period: 0 },
  LIGHT: { type: 'SinceLastCall', period: 120 },
  CORPORATE: { type: 'SinceLastCall', period: 120 },
  PREMIUMBANK: { type: 'SinceLastCall', period: 120 },
  BIRTHDAYS: { type: 'BirthDay', period: null },
};

const countActiveFilters = (values) => {
  if (!values) return 0;
  return Object.keys(values).filter((key) => {
    if (key === 'searchBox' || key === 'sortValue') return false;
    const value = values[key];
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  }).length;
};

/**
 * MobileContacts — the Contatti tab: AllCustomers / Wave / Birthdays. Fetches
 * via getCustomers + useInfiniteQuery (same as desktop), with a list switcher,
 * debounced search and the shared filters sheet. See MOBILE_PLAN.md §F(3,4).
 */
const MobileContacts = function MobileContacts() {
  const translate = useTranslate();
  const API = useAPI();
  const { pathname } = useLocation();
  const selectedBranch = useAtomValue(selectedBranchAtom) || {};
  const branchId = selectedBranch?.id;
  const [searchValues, setSearchValues] = useAtom(searchValuesAtom);

  const [query, setQuery] = useState(searchValues?.searchBox ?? '');
  const [listSheetOpen, setListSheetOpen] = useState(false);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const { findActiveList } = useMobileNavigation();
  const list = findActiveList(pathname);
  const isAllCustomers = list.key === 'CUSTOMERS';
  const showWaveFilters = !isAllCustomers && list.key !== 'BIRTHDAYS';
  const descriptor = LIST_DESCRIPTOR[list.key] ?? LIST_DESCRIPTOR.CUSTOMERS;

  // The long channel is configured through its layouts, which the cog edits on desktop; here they
  // are only read. The short channel has none, and keeps the checked-in filters.
  const isLongChannel =
    isAllCustomers && selectedBranch?.channelType === CHANNEL_TYPES.Long;
  const section = isLongChannel ? LONGCHANNEL_SECTION : null;

  const columnsLayout = useSectionLayout(section, 'list', LONGCHANNEL_COLUMNS);
  const filtersLayout = useSectionLayout(
    section,
    'filters',
    LONGCHANNEL_FILTERS_LAYOUT
  );
  // Birthdays filters by the calls of the day, as on desktop, so it reads the layout that page
  // configures rather than the generic one.
  const isBirthdays = list.key === 'BIRTHDAYS';
  const birthdaysLayout = useSectionLayout(
    isBirthdays ? list.key : null,
    'filters',
    BIRTHDAYS_FILTERS_LAYOUT
  );
  // A saved search layout with no keys hides the box, so an empty one is not a missing one.
  const searchLayout = useSectionLayout(
    section,
    'search',
    LONGCHANNEL_SEARCH,
    null,
    null,
    { allowEmpty: true }
  );

  const card = useMemo(
    () =>
      isLongChannel
        ? resolveCard(columnsLayout, { context: { selectedBranch } })
        : null,
    [isLongChannel, columnsLayout, selectedBranch]
  );

  // The layout the list is filtered by, where the list has one; the others keep the checked-in keys.
  const listFiltersLayout = isLongChannel
    ? filtersLayout
    : isBirthdays
      ? birthdaysLayout
      : null;

  const standardFilters = useMemo(
    () =>
      listFiltersLayout
        ? resolveStandardFilters(listFiltersLayout)
        : CALL_STANDARD_FILTERS,
    [listFiltersLayout]
  );

  const rangeFilters = useMemo(
    () =>
      listFiltersLayout
        ? resolveRangeFilters(listFiltersLayout)
        : RANGE_FILTERS,
    [listFiltersLayout]
  );

  const searchBoxFilters = useMemo(
    () =>
      isLongChannel ? resolveSearchKeys(searchLayout) : SEARCH_BOX_FILTERS,
    [isLongChannel, searchLayout]
  );

  // Debounce the search box into the shared searchValues atom.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchValues((old) => ({
        ...(old || {}),
        searchBox: query || undefined,
      }));
    }, 400);
    return () => clearTimeout(timer);
  }, [query, setSearchValues]);

  const fetchCustomers = useCallback(
    (pageNumber) => {
      if (!branchId) return Promise.resolve([]);
      return API.getCustomers(
        branchId,
        {
          type: descriptor.type,
          period:
            descriptor.period !== null
              ? descriptor.period
              : new Date().toISOString(),
          // The section decides which attributes come back, so the long channel asks for its own.
          sectionName: section ?? list.key,
          wavePeriod: '',
        },
        {
          pageNumber,
          filterExpression: mapFilters(
            searchBoxFilters,
            standardFilters,
            rangeFilters,
            searchValues || {}
          ),
          sortExpression: searchValues?.sortValue
            ? {
                sortValue: searchValues.sortValue.value,
                sort: searchValues.sortValue.direction,
              }
            : null,
          silent: pageNumber > 1,
        },
        () => {},
        () => {}
      );
    },
    [
      branchId,
      list.key,
      section,
      descriptor.type,
      descriptor.period,
      searchValues,
      searchBoxFilters,
      standardFilters,
      rangeFilters,
    ]
  );

  const { ObserverWrapper, recordCount, items, loading } =
    useInfiniteQuery(fetchCustomers);
  const firstLoad = loading && items.length === 0;

  const filterCount = useMemo(
    () => countActiveFilters(searchValues),
    [searchValues]
  );

  return (
    <>
      <MobileHeader
        title={translate(list.labelKey)}
        onTitleClick={() => setListSheetOpen(true)}
        avatar={
          <Box
            sx={{
              minWidth: 34,
              textAlign: 'center',
              fontSize: 13,
              fontWeight: 600,
              color: 'greys.secondary',
              bgcolor: 'background.default',
              px: 1.25,
              py: 0.5,
              borderRadius: '14px',
            }}
          >
            {recordCount && recordCount > 0 ? recordCount.toLocaleString() : 0}
          </Box>
        }
        // A search layout with no keys leaves nothing to match, so the box goes rather than filter
        // nothing.
        search={
          searchBoxFilters.length > 0
            ? {
                value: query,
                onChange: (event) => setQuery(event.target.value),
                placeholder: translate('SEARCH'),
              }
            : undefined
        }
        actions={
          <IconButton
            aria-label={translate('FILTERS')}
            onClick={() => setFilterSheetOpen(true)}
            sx={{
              flex: 'none',
              width: 46,
              height: 46,
              borderRadius: '14px',
              bgcolor: 'background.default',
            }}
          >
            <Badge color="primary" badgeContent={filterCount}>
              <FilterListRoundedIcon />
            </Badge>
          </IconButton>
        }
      />
      {firstLoad ? (
        <SkeletonList count={7} height={64} />
      ) : (
        <ColumnFlexBox sx={{ p: 1.5, gap: 1 }}>
          <ObserverWrapper
            Component={(item) => (
              <ContactCard
                key={item?.id}
                data={item}
                showInactive={isAllCustomers}
                sectionIcon={list.Icon}
                card={card}
              />
            )}
          />
        </ColumnFlexBox>
      )}

      <ListSheet open={listSheetOpen} onClose={() => setListSheetOpen(false)} />
      <FilterSheet
        open={filterSheetOpen}
        onClose={() => setFilterSheetOpen(false)}
        currentSection={list.key}
        showWaveFilters={showWaveFilters}
        filtersLayout={isBirthdays ? birthdaysLayout : null}
        customFilters={
          isLongChannel ? (
            <LongChannelFilters
              layout={filtersLayout}
              defaultValues={searchValues}
              onConfirm={(values) =>
                setSearchValues((old) => ({ ...(old || {}), ...values }))
              }
              branchId={branchId}
              sectionName={LONGCHANNEL_SECTION}
            />
          ) : null
        }
      />
    </>
  );
};

export default MobileContacts;
